/**
 * Sprint 23.5 — HireDraftPanel.
 *
 * The right side of the split-screen wizard: a scrollable list of fields,
 * each editable directly (pencil) or regeneratable via AI (sparkle). The
 * footer holds Cancel + Hire. Each regenerate hook is provided by the page
 * — it queues a focused chat turn to re-emit `update_composition_draft`.
 */

import type { CSSProperties } from "react";
import type { LibraryNode } from "../../../lib/types";
import { HireDraftField } from "./HireDraftField";
import { HireDraftStyleSelect } from "./HireDraftStyleSelect";
import { HireDraftSpecialtiesTags } from "./HireDraftSpecialtiesTags";
import { HireDraftEquippedWith } from "./HireDraftEquippedWith";
import { HireDraftReportsTo } from "./HireDraftReportsTo";
import { HireDraftFooter } from "./HireDraftFooter";
import { canHire, type HireDraft, type HirePersonaStyle } from "./types";

export interface HireDraftPanelProps {
  draft: HireDraft;
  onChange: <K extends keyof HireDraft>(field: K, value: HireDraft[K]) => void;
  onRegenerateField: (field: keyof HireDraft) => void;
  onCancel: () => void;
  onHire: () => void;
  busy: boolean;
  hireLabel: string;
  rootNodeId: string | null;
  workspaceName: string;
  allNodes: LibraryNode[];
}

export function HireDraftPanel({
  draft,
  onChange,
  onRegenerateField,
  onCancel,
  onHire,
  busy,
  hireLabel,
  rootNodeId,
  workspaceName,
  allNodes,
}: HireDraftPanelProps) {
  return (
    <div style={panel}>
      <div style={scroll}>
        {/* Ordering follows the field flow approved 2026-06-22 (Q2):
            name → role → bio → persona → tasks (deferred) → reports-to → equipped. */}
        <HireDraftField
          label="Name"
          value={draft.name}
          placeholder="e.g. Sarah, Helix, Atlas"
          onChange={(v) => onChange("name", v)}
          onRegenerate={() => onRegenerateField("name")}
        />
        <HireDraftField
          label="Role"
          value={draft.role}
          placeholder="e.g. Sales research analyst"
          onChange={(v) => onChange("role", v)}
          onRegenerate={() => onRegenerateField("role")}
        />
        <HireDraftField
          label="Bio"
          value={draft.bio}
          placeholder="Who they are and what they bring"
          multiline
          onChange={(v) => onChange("bio", v)}
          onRegenerate={() => onRegenerateField("bio")}
        />
        <HireDraftStyleSelect
          value={draft.style}
          onChange={(v: HirePersonaStyle | null) => onChange("style", v)}
        />
        <HireDraftField
          label="Voice / tone"
          value={draft.voice_tone}
          placeholder="e.g. warm but direct"
          onChange={(v) => onChange("voice_tone", v)}
          onRegenerate={() => onRegenerateField("voice_tone")}
        />
        <HireDraftField
          label="Motivations"
          value={draft.motivations}
          placeholder="What this agent cares about / optimises for"
          multiline
          onChange={(v) => onChange("motivations", v)}
          onRegenerate={() => onRegenerateField("motivations")}
        />
        <HireDraftSpecialtiesTags
          value={draft.specialties}
          onChange={(v) => onChange("specialties", v)}
        />
        <HireDraftReportsTo
          value={draft.reports_to}
          rootNodeId={rootNodeId}
          workspaceName={workspaceName}
          allNodes={allNodes}
          onChange={(v) => onChange("reports_to", v)}
        />
        <HireDraftEquippedWith
          value={draft.equipped_with}
          onChange={(v) => onChange("equipped_with", v)}
        />
      </div>
      <HireDraftFooter
        hireLabel={hireLabel}
        hireName={draft.name.trim()}
        canHire={canHire(draft)}
        busy={busy}
        onCancel={onCancel}
        onHire={onHire}
      />
    </div>
  );
}

const panel: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  background: "var(--color-background-primary)",
  fontFamily: "var(--font-sans)",
};

const scroll: CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  padding: "16px 18px",
};
